'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
const bgMusicUrl = '/audio/merge-background-audio.mp3'

const MUSIC_VOLUME = 0.18       // Background music target volume
const FADE_DURATION = 1.5       // Seconds for fade in/out
const TICK_VOLUME = 0.03        // UI tick volume

interface SoundManager {
  isEnabled: boolean
  toggle: () => void
  playTick: () => void
  startAmbient: () => void
  stopAmbient: () => void
}

export function useSound(): SoundManager {
  const [isEnabled, setIsEnabled] = useState(false)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const musicSourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const musicGainRef = useRef<GainNode | null>(null)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const isStartedRef = useRef(false)
  const firstInteractionRef = useRef(false)

  // Initialize AudioContext
  const initAudio = useCallback(() => {
    if (audioCtxRef.current) return audioCtxRef.current
    const ctx = new AudioContext()
    audioCtxRef.current = ctx
    return ctx
  }, [])

  // Start background music (mp3 file)
  const startAmbient = useCallback(() => {
    if (isStartedRef.current && audioElRef.current) {
      // Already started, just resume and fade in
      const ctx = audioCtxRef.current
      if (ctx && ctx.state === 'suspended') ctx.resume()
      audioElRef.current.play().catch(() => {})
      if (musicGainRef.current && ctx) {
        musicGainRef.current.gain.cancelScheduledValues(ctx.currentTime)
        musicGainRef.current.gain.setValueAtTime(musicGainRef.current.gain.value, ctx.currentTime)
        musicGainRef.current.gain.linearRampToValueAtTime(MUSIC_VOLUME, ctx.currentTime + FADE_DURATION)
      }
      return
    }

    const ctx = initAudio()
    if (ctx.state === 'suspended') ctx.resume().catch(() => {})

    // Create audio element for the mp3
    const audio = new Audio(bgMusicUrl)
    audio.loop = true
    audio.crossOrigin = 'anonymous'
    audioElRef.current = audio

    // Connect through Web Audio API gain node for smooth volume control
    const source = ctx.createMediaElementSource(audio)
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, ctx.currentTime)
    gain.gain.linearRampToValueAtTime(MUSIC_VOLUME, ctx.currentTime + FADE_DURATION)

    source.connect(gain)
    gain.connect(ctx.destination)

    musicSourceRef.current = source
    musicGainRef.current = gain
    isStartedRef.current = true

    // Play — AudioContext is always created inside a user-gesture handler so resume is allowed
    ctx.resume().catch(() => {})
    audio.play().catch(() => {})
  }, [initAudio])

  // Stop / fade out background music
  const stopAmbient = useCallback(() => {
    const ctx = audioCtxRef.current
    if (musicGainRef.current && ctx) {
      musicGainRef.current.gain.cancelScheduledValues(ctx.currentTime)
      musicGainRef.current.gain.setValueAtTime(musicGainRef.current.gain.value, ctx.currentTime)
      musicGainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6)
      setTimeout(() => {
        audioElRef.current?.pause()
      }, 700)
    }
  }, [])

  // UI tick sound
  const playTick = useCallback(() => {
    if (!isEnabled) return
    const ctx = audioCtxRef.current
    if (!ctx) return

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.setValueAtTime(800, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.05)

    gain.gain.setValueAtTime(TICK_VOLUME, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08)

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.1)
  }, [isEnabled])

  const toggle = useCallback(() => {
    setIsEnabled(prev => {
      const next = !prev
      if (next) {
        startAmbient()
      } else {
        stopAmbient()
      }
      return next
    })
  }, [startAmbient, stopAmbient])

  // Auto-play on first user interaction (click/touch/keydown anywhere)
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (firstInteractionRef.current) return
      firstInteractionRef.current = true

      // Auto-enable sound on first interaction
      setIsEnabled(true)
      startAmbient()

      // Remove listeners after first trigger
      window.removeEventListener('click', handleFirstInteraction)
      window.removeEventListener('touchstart', handleFirstInteraction)
      window.removeEventListener('keydown', handleFirstInteraction)
    }

    window.addEventListener('click', handleFirstInteraction, { once: true })
    window.addEventListener('touchstart', handleFirstInteraction, { once: true })
    window.addEventListener('keydown', handleFirstInteraction, { once: true })

    return () => {
      window.removeEventListener('click', handleFirstInteraction)
      window.removeEventListener('touchstart', handleFirstInteraction)
      window.removeEventListener('keydown', handleFirstInteraction)
    }
  }, [startAmbient])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioElRef.current?.pause()
      audioElRef.current = null
      audioCtxRef.current?.close()
    }
  }, [])

  return { isEnabled, toggle, playTick, startAmbient, stopAmbient }
}
